import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Loader, Modal, TextField, notifications } from "@/shared/ui";
import { IconMicrophone, IconPencil, IconTrash, IconPlus, IconWaveSine, } from "@tabler/icons-react";
import { listVoiceProfiles, getVoiceProfile, deleteVoiceProfile, renameVoiceProfile, isVoiceProfileReady, isVoiceProfileFailed, isVoiceProfileAwaitingVerify, } from "@/shared/api/voice";
import { useAutoReadVoiceReady } from "@/features/notification-center";
import { VoiceCloneRecorder } from "./VoiceCloneRecorder";
import styles from "./VoiceStudioModal.module.scss";
const statusLabel = (status, ru) => {
    const s = String(status).toLowerCase();
    if (s === "ready")
        return { text: ru ? "Готов" : "Ready", tone: "ready" };
    if (s === "failed")
        return { text: ru ? "Ошибка" : "Failed", tone: "failed" };
    if (s === "awaiting_verify")
        return {
            text: ru ? "Ждёт пения фразы" : "Awaiting phrase",
            tone: "progress",
        };
    return { text: ru ? "Обрабатывается…" : "Processing…", tone: "progress" };
};
/**
 * Менеджер голосов (вход из хедера «Мои песни»): список сохранённых профилей
 * (свой голос / ИИ-персона) с удалением + запуск записи нового своего голоса
 * (VoiceCloneRecorder). Только сайт — родитель гейтит по платформе.
 */
export const VoiceStudioModal = ({ opened, onClose, language, initialVoiceVerifyId, }) => {
    const ru = language === "ru";
    const [view, setView] = useState("list");
    const [profiles, setProfiles] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    // Профиль, для которого показываем подтверждение удаления (null — диалог скрыт).
    const [confirmTarget, setConfirmTarget] = useState(null);
    // Профиль, для которого показываем диалог переименования (null — диалог скрыт).
    const [renameTarget, setRenameTarget] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [savingRename, setSavingRename] = useState(false);
    // Профиль для возобновления (resume) записи: передаётся в VoiceCloneRecorder,
    // который продолжает флоу с его статуса (обычно AWAITING_VERIFY → пение фразы).
    const [resumeProfile, setResumeProfile] = useState(null);
    // Идёт подтягивание полного профиля (клик по строке / deep-link) — блокируем
    // повторные клики и показываем лоадер на строке.
    const [resumingId, setResumingId] = useState(null);
    // Пока менеджер голосов открыт — пользователь ждёт свой голос на экране;
    // приходящие «голос готов» помечаем прочитанными (не копим в бейдже).
    useAutoReadVoiceReady(opened);
    const loadProfiles = useCallback(async () => {
        setLoadError(false);
        try {
            const list = await listVoiceProfiles();
            setProfiles(list);
        }
        catch {
            setLoadError(true);
            setProfiles([]);
        }
    }, []);
    // Грузим список при каждом открытии и возврате к списку — статусы всегда
    // свежие (профили серверные; клон/персона дообрабатываются на бэке).
    useEffect(() => {
        if (opened && view === "list")
            void loadProfiles();
    }, [opened, view, loadProfiles]);
    // Лёгкий поллинг незавершённых профилей, пока открыт список: in-progress
    // (validating/awaiting_verify/processing) обновляются без ручного refetch.
    useEffect(() => {
        if (!opened || view !== "list")
            return;
        const hasInProgress = (profiles ?? []).some((p) => !isVoiceProfileReady(p) && !isVoiceProfileFailed(p));
        if (!hasInProgress)
            return;
        const timer = setInterval(() => {
            void loadProfiles();
        }, 5000);
        return () => clearInterval(timer);
    }, [opened, view, profiles, loadProfiles]);
    // Сброс на список при закрытии — следующее открытие начинается с обзора.
    useEffect(() => {
        if (!opened) {
            setView("list");
            setConfirmTarget(null);
            setRenameTarget(null);
            setRenameValue("");
            setSavingRename(false);
            setResumeProfile(null);
            setResumingId(null);
        }
    }, [opened]);
    // Возобновление флоу для профиля: тянем полный профиль (в списке `phrase` не
    // приходит) и открываем resume-рекордер. Не-AWAITING_VERIFY тоже отдаём в
    // рекордер — он покажет корректный экран статуса (processing/failed/ready).
    const openResume = useCallback(async (id) => {
        setResumingId(id);
        try {
            const full = await getVoiceProfile(id);
            setResumeProfile(full);
            setView("record");
        }
        catch {
            notifications.show({
                title: ru ? "Ошибка" : "Error",
                message: ru
                    ? "Не удалось открыть голос"
                    : "Failed to open the voice",
                color: "red",
            });
        }
        finally {
            setResumingId(null);
        }
    }, [ru]);
    // Deep-link `?voice_verify=<id>`: при открытии подтягиваем профиль и, если он
    // ждёт пения фразы, сразу открываем resume-рекордер. Иначе остаёмся на списке
    // (пользователь увидит актуальный статус). Обрабатываем один раз на открытие.
    const handledVerifyId = useRef(null);
    useEffect(() => {
        if (!opened) {
            handledVerifyId.current = null;
            return;
        }
        const id = initialVoiceVerifyId;
        if (id == null || handledVerifyId.current === id)
            return;
        handledVerifyId.current = id;
        (async () => {
            try {
                const full = await getVoiceProfile(id);
                if (isVoiceProfileAwaitingVerify(full)) {
                    setResumeProfile(full);
                    setView("record");
                }
            }
            catch {
                /* профиль недоступен — тихо остаёмся на списке */
            }
        })();
    }, [opened, initialVoiceVerifyId]);
    const handleDelete = async (id) => {
        setDeletingId(id);
        try {
            await deleteVoiceProfile(id);
            setProfiles((prev) => prev?.filter((p) => p.id !== id) ?? null);
        }
        catch {
            notifications.show({
                title: ru ? "Ошибка" : "Error",
                message: ru ? "Не удалось удалить голос" : "Failed to delete voice",
                color: "red",
            });
        }
        finally {
            setDeletingId(null);
        }
    };
    const handleRename = async () => {
        const target = renameTarget;
        if (!target)
            return;
        const trimmed = renameValue.trim();
        if (!trimmed)
            return;
        setSavingRename(true);
        try {
            const updated = await renameVoiceProfile(target.id, trimmed);
            setProfiles((prev) => prev?.map((p) => p.id === target.id ? { ...p, name: updated.name ?? trimmed } : p) ?? null);
            setRenameTarget(null);
        }
        catch {
            notifications.show({
                title: ru ? "Ошибка" : "Error",
                message: ru
                    ? "Не удалось переименовать голос"
                    : "Failed to rename voice",
                color: "red",
            });
        }
        finally {
            setSavingRename(false);
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Modal, { opened: opened, onClose: onClose, size: "md", title: ru ? "Мой голос" : "My voice", children: view === "record" ? (_jsx(VoiceCloneRecorder, { language: language, resumeProfile: resumeProfile ?? undefined, onBack: () => {
                        setResumeProfile(null);
                        setView("list");
                    }, onCreated: () => {
                        // Список обновится при возврате к нему (эффект по view).
                    } })) : (_jsxs("div", { className: styles.list, children: [_jsx("p", { className: styles.intro, children: ru
                                ? "Сохраните свой голос и пойте им новые песни. Создать ИИ-голос из готового трека можно из меню трека в списке песен."
                                : "Save your voice and sing new songs with it. You can also create an AI voice from a finished track via the track menu." }), loadError && (_jsx(Alert, { tone: "error", children: ru ? "Не удалось загрузить голоса" : "Failed to load voices" })), profiles == null ? (_jsx("div", { className: styles.loaderRow, children: _jsx(Loader, { size: "md" }) })) : profiles.length === 0 ? (_jsxs("div", { className: styles.empty, children: [_jsx(IconWaveSine, { size: 36, className: styles.emptyIcon }), _jsx("div", { className: styles.emptyTitle, children: ru ? "Пока нет голосов" : "No voices yet" }), _jsx("div", { className: styles.emptyHint, children: ru
                                        ? "Запишите свой голос — он появится здесь."
                                        : "Record your voice — it will appear here." })] })) : (_jsx("ul", { className: styles.profiles, children: profiles.map((p) => {
                                const st = statusLabel(p.status, ru);
                                // Профиль, ждущий пения фразы, — кликабелен: продолжаем флоу
                                // (открываем resume-рекордер на шаге пения контрольной фразы).
                                const canResume = isVoiceProfileAwaitingVerify(p);
                                const isResuming = resumingId === p.id;
                                return (_jsxs("li", { className: styles.profileRow, children: [_jsx("span", { className: styles.profileIcon, children: isResuming ? (_jsx(Loader, { size: "sm" })) : (_jsx(IconMicrophone, { size: 18 })) }), _jsxs("div", { className: styles.profileInfo, role: canResume ? "button" : undefined, tabIndex: canResume ? 0 : undefined, style: canResume ? { cursor: "pointer" } : undefined, onClick: canResume && !isResuming
                                                ? () => void openResume(p.id)
                                                : undefined, onKeyDown: canResume && !isResuming
                                                ? (e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        void openResume(p.id);
                                                    }
                                                }
                                                : undefined, children: [_jsx("span", { className: styles.profileName, children: p.name ||
                                                        (ru ? "Голос без имени" : "Unnamed voice") }), _jsxs("span", { className: styles.profileMeta, children: [_jsx("span", { className: styles.kindBadge, children: p.kind === "persona"
                                                                ? ru
                                                                    ? "ИИ из трека"
                                                                    : "AI from track"
                                                                : ru
                                                                    ? "Свой голос"
                                                                    : "Voice clone" }), _jsx("span", { className: `${styles.statusBadge} ${styles[st.tone]}`, children: canResume
                                                                ? ru
                                                                    ? "Продолжить запись"
                                                                    : "Continue recording"
                                                                : st.text })] })] }), _jsx("button", { type: "button", className: styles.renameBtn, "aria-label": ru ? "Переименовать" : "Rename", onClick: () => {
                                                setRenameValue(p.name || "");
                                                setRenameTarget(p);
                                            }, children: _jsx(IconPencil, { size: 16 }) }), _jsx("button", { type: "button", className: styles.deleteBtn, "aria-label": ru ? "Удалить" : "Delete", disabled: deletingId === p.id, onClick: () => setConfirmTarget(p), children: deletingId === p.id ? (_jsx(Loader, { size: "sm" })) : (_jsx(IconTrash, { size: 16 })) })] }, p.id));
                            }) })), _jsx("div", { className: styles.footer, children: _jsx(Button, { variant: "primary", size: "md", leftSection: _jsx(IconPlus, { size: 16 }), onClick: () => {
                                    setResumeProfile(null);
                                    setView("record");
                                }, children: ru ? "Записать свой голос" : "Record my voice" }) })] })) }), _jsxs(Modal, { opened: confirmTarget != null, onClose: () => setConfirmTarget(null), size: "sm", title: ru ? "Удалить голос?" : "Delete voice?", children: [_jsx("p", { className: styles.deleteText, children: ru
                            ? `Удалить голос «${confirmTarget?.name || "Голос без имени"}»? Это действие необратимо.`
                            : `Delete voice "${confirmTarget?.name || "Unnamed voice"}"? This cannot be undone.` }), _jsxs("div", { className: styles.deleteActions, children: [_jsx(Button, { variant: "ghost", size: "md", onClick: () => setConfirmTarget(null), children: ru ? "Отмена" : "Cancel" }), _jsx(Button, { variant: "primary", size: "md", onClick: () => {
                                    const target = confirmTarget;
                                    setConfirmTarget(null);
                                    if (target)
                                        void handleDelete(target.id);
                                }, children: ru ? "Удалить" : "Delete" })] })] }), _jsxs(Modal, { opened: renameTarget != null, onClose: () => setRenameTarget(null), size: "sm", title: ru ? "Переименовать голос" : "Rename voice", children: [_jsx(TextField, { label: ru ? "Название голоса" : "Voice name", value: renameValue, onChange: (e) => setRenameValue(e.target.value), maxLength: 100, "data-autofocus": true }), _jsxs("div", { className: styles.deleteActions, children: [_jsx(Button, { variant: "ghost", size: "md", onClick: () => setRenameTarget(null), children: ru ? "Отмена" : "Cancel" }), _jsx(Button, { variant: "primary", size: "md", loading: savingRename, disabled: renameValue.trim() === "" ||
                                    renameValue.trim() === (renameTarget?.name ?? "") ||
                                    savingRename, onClick: () => void handleRename(), children: ru ? "Сохранить" : "Save" })] })] })] }));
};
